/**
 * A hand-rolled bar chart, the same contract as LineChart.tsx: it formats
 * nothing, decides nothing, and takes every string from the `BarPlan` and every
 * coordinate from lib/chartScale.ts.
 *
 * BARS RATHER THAN A LINE because the series it renders (`ebayCallsPerDay`) is
 * a count PER DISCRETE DAY, not a sample of a continuous quantity: a line drawn
 * between two daily totals draws values that were never measured.
 */
import {
  CHART_BASELINE, CHART_HEIGHT, CHART_PAD_TOP, CHART_PAD_X, CHART_WIDTH,
  barHeight, chartMax,
} from '../../lib/chartScale';
import type { BarPlan } from '../../lib/metrics';

export function BarChart({ plan }: { plan: BarPlan }) {
  const points = plan.points;
  if (points.length === 0) return null;

  // Scaled by the MAXIMUM. Dividing by `points.length` instead is the same
  // expression with the bars scaled by how many days are in the window, and it
  // produces perfectly finite, perfectly wrong heights.
  const max = chartMax(points.map((p) => p.value));
  const slot = (CHART_WIDTH - 2 * CHART_PAD_X) / points.length;
  const width = Math.max(slot * 0.7, 1);
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <svg
      className="chart chart-bar"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      role="img"
      aria-label={plan.ariaLabel}
      preserveAspectRatio="none"
    >
      <title>{plan.ariaLabel}</title>
      <line x1={CHART_PAD_X} y1={CHART_BASELINE} x2={CHART_WIDTH - CHART_PAD_X} y2={CHART_BASELINE}
        stroke="currentColor" strokeOpacity="0.25" />
      {points.map((p, i) => {
        const height = barHeight(p.value, max);
        return (
          <rect
            key={`${p.label}-${i}`}
            x={CHART_PAD_X + i * slot + (slot - width) / 2}
            y={CHART_BASELINE - height}
            width={width}
            height={height}
            fill="var(--accent)"
          >
            <title>{p.title}</title>
          </rect>
        );
      })}
      <text x={CHART_PAD_X} y={CHART_HEIGHT - 8} fontSize="12" fill="currentColor" fillOpacity="0.7">
        {first?.label ?? ''}
      </text>
      <text x={CHART_WIDTH - CHART_PAD_X} y={CHART_HEIGHT - 8} fontSize="12" textAnchor="end"
        fill="currentColor" fillOpacity="0.7">
        {last?.label ?? ''}
      </text>
      <text x={CHART_PAD_X} y={CHART_PAD_TOP} fontSize="12" fill="currentColor" fillOpacity="0.7">
        {plan.maxLabel}
      </text>
    </svg>
  );
}
